import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Settings,
  Language,
  Business,
  Inventory,
  Assessment,
  Notifications,
  ChevronLeft,
  ChevronRight,
  Dashboard,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBuildVersion } from '../../utils/buildVersion';
import authService from '../../services/authService';

const DRAWER_FULL = 260;
const DRAWER_MINI = 64;

type NavItem = { key: string; label: string; icon: JSX.Element; path: string };

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { changeLanguage } = useLanguage();

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  React.useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { default: notificationsApi } = await import('../../services/notificationService');
        const count = await notificationsApi.unreadCount();
        if (!cancelled) setUnreadCount(count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const profile = user?.profile || 'ADMIN';
  const allNavItems: (NavItem & { profiles?: string[] })[] = [
    { key: 'dashboard', label: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { key: 'evaluations', label: t('menu.evaluations', 'Evaluations'), icon: <Assessment />, path: '/evaluations' },
    { key: 'companies', label: t('menu.companies', 'Companies'), icon: <Business />, path: '/companies', profiles: ['ADMIN', 'APPROVER'] },
    { key: 'price-list', label: t('menu.priceList', 'Price List'), icon: <Inventory />, path: '/price-list', profiles: ['ADMIN', 'APPROVER'] },
    { key: 'notifications', label: t('menu.notifications', 'Notifications'), icon: <Notifications />, path: '/notifications' },
    { key: 'settings', label: t('menu.settings'), icon: <Settings />, path: '/settings', profiles: ['ADMIN'] },
  ];
  const navItems = allNavItems.filter(item => !item.profiles || item.profiles.includes(profile));

  const initial = (): string => {
    if (!user?.name) return '?';
    return (user.name.trim()[0] || '?').toUpperCase();
  };

  const handleCloseLang = (lang: string) => {
    changeLanguage(lang);
    setLangAnchor(null);
    authService.updateMyProfile({ preferred_language: lang }).catch(() => {});
  };

  const currentWidth = collapsed ? DRAWER_MINI : DRAWER_FULL;

  const drawerContent = (mini: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Toolbar sx={{ minHeight: { xs: 48, sm: 56 }, justifyContent: mini ? 'center' : 'flex-start', px: mini ? 0 : 2 }}>
        {!mini && (
          <Typography variant="h6" fontWeight={600} color="primary" noWrap>
            Prototype
          </Typography>
        )}
        {mini && (
          <Typography variant="h6" fontWeight={600} color="primary">P</Typography>
        )}
      </Toolbar>
      <List sx={{ flex: 1, px: mini ? 0.5 : 0 }}>
        {navItems.map((item) => {
          const btn = (
            <ListItemButton
              selected={location.pathname.startsWith(item.path)}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                justifyContent: mini ? 'center' : 'flex-start',
                px: mini ? 1.5 : 2,
                minHeight: 44,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: mini ? 0 : 40, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              {!mini && <ListItemText primary={item.label} />}
            </ListItemButton>
          );
          return (
            <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
              {mini ? (
                <Tooltip title={item.label} placement="right" arrow>
                  {btn}
                </Tooltip>
              ) : btn}
            </ListItem>
          );
        })}
      </List>

      {user && !mini && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
              p: 2,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.3)', color: 'inherit', fontSize: '0.9rem' }}>
                {initial()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {user.name} {user.surname}
                </Typography>
                <Typography variant="caption" noWrap sx={{ opacity: 0.9 }}>
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <ListItemButton
            onClick={() => { logout(); navigate('/login'); }}
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('menu.logout')} />
          </ListItemButton>
        </Box>
      )}

      {user && mini && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={`${user.name} ${user.surname}`} placement="right" arrow>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.85rem' }}>
              {initial()}
            </Avatar>
          </Tooltip>
          <Tooltip title={t('menu.logout')} placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => { logout(); navigate('/login'); }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'space-between', px: mini ? 0 : 2, pb: 1 }}>
        {!mini && (
          <Typography variant="caption" color="text.secondary">
            Build #{getBuildVersion()}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={1}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56 } }}>
          <IconButton
            color="inherit"
            size="small"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ display: { sm: 'none' }, mr: 1 }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="inherit"
            size="small"
            onClick={toggleCollapsed}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1 }}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap component="div" sx={{ flex: 1 }}>
            Prototype
          </Typography>
          <IconButton color="inherit" size="small" onClick={() => navigate('/notifications')} sx={{ mr: 0.5 }}>
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>
          <IconButton color="inherit" size="small" onClick={(e) => setLangAnchor(e.currentTarget)}>
            <Language fontSize="small" />
          </IconButton>
          <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
            <MenuItem onClick={() => handleCloseLang('es')}>Español</MenuItem>
            <MenuItem onClick={() => handleCloseLang('en')}>English</MenuItem>
          </Menu>
          <IconButton color="inherit" size="small" sx={{ ml: 0.25 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.85rem', bgcolor: 'rgba(255,255,255,0.3)' }}>{initial()}</Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: currentWidth }, flexShrink: { sm: 0 }, transition: 'width 0.2s' }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_FULL } }}
        >
          {drawerContent(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentWidth,
              transition: 'width 0.2s',
              overflowX: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawerContent(collapsed)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: { xs: 6, sm: 7 },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: 'margin-left 0.2s',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
